/**
 * User Account System Type Definitions
 * Types for user profiles, authentication state, and activity tracking
 */

/**
 * Account status enumeration
 */
export type AccountStatus = 'active' | 'suspended' | 'deleted';

/**
 * Subscription tier enumeration
 */
export type SubscriptionTier = 'free' | 'premium' | 'enterprise';

/**
 * User preferences interface
 * Stores user-specific settings and feature flags
 */
export interface UserPreferences {
  theme?: 'light' | 'dark' | 'auto';
  notifications?: boolean;
  [key: string]: any;
}

/**
 * User metadata interface
 * Extensible custom attributes for analytics and tracking
 */
export interface UserMetadata {
  referralSource?: string;
  signupCampaign?: string;
  [key: string]: any;
}

/**
 * Activity metrics interface
 * Tracks user composition and interaction events
 */
export interface ActivityMetrics {
  aiGenerateCount: number;
  manualComposeCount: number;
  compositionEditCount: number;
  compositionShareCount: number;
}

/**
 * Complete user profile interface
 * Represents the full user account data
 */
export interface UserProfile {
  userId: string;
  email: string;
  name: string;
  givenName?: string;
  familyName?: string;
  profilePictureUrl?: string;
  emailVerified: boolean;
  authProvider: string;
  accountStatus: AccountStatus;
  subscriptionTier: SubscriptionTier;
  subscriptionExpiresAt?: string;
  currentCredits: number;
  preferences: UserPreferences;
  metadata: UserMetadata;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string;
  lastActivityAt: string;
  // Activity metrics
  aiGenerateCount?: number;
  manualComposeCount?: number;
  compositionEditCount?: number;
  compositionShareCount?: number;
  // Composition library
  compositions?: Array<{
    compositionId: string;
    title: string;
    s3Key: string;
    createdAt: string;
    updatedAt: string;
    instrumentCount: number;
    patternLength: number;
    tempo: number;
    noteCount: number;
  }>;
  // Effects presets
  effectsPresets?: Array<{
    id: string;
    name: string;
    description?: string | undefined;
    category?: string | undefined;
    effects: Array<{
      id: string;
      type: string;
      parameters: Record<string, number>;
      bypassed: boolean;
      slot: number;
    }>;
    isFavorite: boolean;
    isDefault: boolean;
    createdAt: string;
    updatedAt: string;
  }>;
  // Admin and usage tracking
  isAdmin?: boolean;
  totalCreditsUsed?: number;
  // Email marketing consent
  emailMarketingConsent?: boolean;
  emailMarketingConsentAt?: string;
  emailMarketingConsentVersion?: string;
}

/**
 * User profile state interface
 * Represents the state of user profile data in the application
 */
export interface UserProfileState {
  profile: UserProfile | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * User profile error types enumeration
 */
export enum UserProfileErrorType {
  NOT_AUTHENTICATED = 'NOT_AUTHENTICATED',
  INVALID_TOKEN = 'INVALID_TOKEN',
  USER_NOT_FOUND = 'USER_NOT_FOUND',
  SYNC_FAILED = 'SYNC_FAILED',
  UPDATE_FAILED = 'UPDATE_FAILED',
  NETWORK_ERROR = 'NETWORK_ERROR',
  DYNAMODB_ERROR = 'DYNAMODB_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
}

/**
 * User profile error class
 * Custom error class for user profile operations
 */
export class UserProfileError extends Error {
  public type: UserProfileErrorType;
  public statusCode?: number;
  public originalError?: unknown;

  constructor(
    type: UserProfileErrorType,
    message: string,
    statusCode?: number,
    originalError?: unknown
  ) {
    super(message);
    this.name = 'UserProfileError';
    this.type = type;
    if (statusCode !== undefined) {
      this.statusCode = statusCode;
    }
    if (originalError !== undefined) {
      this.originalError = originalError;
    }

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, UserProfileError);
    }
  }
}
